import { MKUtils } from "../common/MKUtils";
import { MKHttpRequest } from "./MKHttpRequest";
import { MKAssetType, MKRightsMode } from "../../enums";
import { MKAzukiRequestError, MKHttpError } from "../error";
import { MKHeaders, MKRequestQueryParams, MKUserEngagement } from "../../alias"; 
import { MKAzukiRequestEndpoint, MKConstants } from "../../constants";
import { MKAzukiResponseUtils, MKLocalStorage, MKLog, MKPlatform } from "../common";
import { MKProgramInfo, MKSourceConfig, MKRollInfo, MKHttpResponse, MKSessionConcurrencyAPI, MKError, MKAdobePrimetimeConfig, MKPlayerConfig } from "../../interfaces";
import { MKSecureHeadersManager } from "../common/MKSecureHeadersManager";

/**
 * Azuki (TVP Backend) requests.
 * @internal
 */
export class MKAzukiRequest extends MKHttpRequest implements MKSessionConcurrencyAPI {
    // Set the session manager to azuki
    public sessionManager: "session_manager_adobe" | "session_manager_azuki" = "session_manager_azuki";
    public isPaused: boolean = false;
    
    // common URL query params for the backend request
    private mediaId: string;
    private ownerUid: string;
    private sessionId: string;
    private offerId?: string;
    private isDvrFromRollingBuffer: boolean;
    private lowLatency: boolean;

    // request server baseUrl
    private baseUrl: string;

    // player config
    private playerConfig: MKPlayerConfig;

    // request headers
    private headers: MKHeaders;

    // all other variables
    private isLive: boolean;
    private beaconInterval: number;
    private rightsMode: MKRightsMode;
    private startTime?: string;  // catchup or DVR (from rolling buffer) start time.
    private localStorage: MKLocalStorage;
    private errorCallback?: (error: MKError) => void;
    private beaconTimer?: ReturnType<typeof setInterval>;
    private adobePrimetimeConfig?: MKAdobePrimetimeConfig;
    private registrationRetryflag: boolean = false;
    private secureHeadersManager?: MKSecureHeadersManager;
    private kssUrl?: string;
    static relativePlaybackLocation?: number = -1;
    static startPlaybackPosition?: number;
    static enableUserEngagementData?: boolean;
    static playerState?: string;

    // static settings
    // these settings outlive the session and should
    // remain unchanged unless set again hence static
    static locationDetail?: string;
    static inhomeStatus: boolean = false;
    static responseTime = {    // time taken for the request to complete in milliseconds
        registration: 0,
        roll: 0,
        beacons: 0,
        pendingBeacons: 0,
        query: 0
    };
    static customHeaders: MKHeaders | null;
    static userEngagementData: MKUserEngagement | null;
    static cloudIdInfo?: string | null;

    /**
     * Azuki (TVP Backend) requests.
     * @param sourceConfig current source configuration object.
     * @param errorCallback error callback to get notified when error occurs during beacon (session concurrecny) notifications.
     */
    constructor(sourceConfig: MKSourceConfig, playerConfig: MKPlayerConfig, errorCallback?: (error: MKError) => void) {
        super();
        this.playerConfig = playerConfig;
        this.beaconInterval = 0;
        this.errorCallback = errorCallback;
        this.localStorage = MKLocalStorage.getInstance();
        this.mediaId = sourceConfig.registered?.mediaId ?? "";
        this.ownerUid = sourceConfig.registered?.environmentConfig.ownerUid ?? MKConstants.AzukiDefaultOwnerUid;
        this.sessionId = sourceConfig.sessionId ?? MKUtils.getUUID();
        this.offerId = sourceConfig.registered?.offerId;
        this.isDvrFromRollingBuffer = sourceConfig.registered?.isDvrFromRollingBuffer ?? false;
        this.baseUrl = sourceConfig.registered?.environmentConfig.serverUrl ?? "";
        this.headers = this.getRequestHeaders(sourceConfig);
        this.isLive = (sourceConfig.assetType === MKAssetType.Live || sourceConfig.assetType === MKAssetType.Event) ?? false;
        this.rightsMode = this.isLive ? MKRightsMode.Live : MKRightsMode.VOD;
        this.adobePrimetimeConfig = sourceConfig.registered?.adobePrimetimeConfig;
        this.startTime = !!sourceConfig.registered?.startTime && (sourceConfig.assetType === MKAssetType.Catchup || (sourceConfig.assetType === MKAssetType.DVR && this.isDvrFromRollingBuffer)) ? sourceConfig.registered.startTime : undefined;
        this.lowLatency = sourceConfig.enableLowLatency ?? false;
        this.kssUrl = sourceConfig?.registered?.environmentConfig.kssUrl;
    }


    /**
     * Update STS authToken.
     * 
     * @param authToken the new authToken.
     */
    updateAuthToken(authToken: string): void {
        if (!!authToken) {
            this.headers["AuthorizationToken"] = authToken;
        }
    }

    /**
     * Get beacon interval.
     *
     * @returns beacon interval in seconds.
     */
    getBeaconInterval(): number {
        return this.beaconInterval;
    }

    /**
     * Get saved beacon data from local storage if available.
     *
     * @returns an object containing beacon data if available else `null` is returned.
     */
    getBeaconData(): any {
        return this.localStorage.getBeaconData();
    }

    /**
     * Get azuki sessionId.
     * 
     * @returns azuki sessionId
     */
    getAzukiSessionId(): string {
        return this.sessionId || "";
    }

    /**
     * Registration request.
     * @returns resolved Promise<void> on success and rejected with error code and message on failure.
     */
    async registrationRequest(): Promise<void> {
        // Check and add device profile to headers
        if (!!!this.headers["DeviceProfile"]) {
            const deviceProfile = await MKPlatform.getDeviceProfile();
            if (!!deviceProfile) this.headers["DeviceProfile"] = deviceProfile;
        }

        return new Promise<void>((resolve, reject) => {
            this.post(MKAzukiRequestEndpoint.REGISTRATION.get(this.baseUrl), {
                headers: this.headers,
                queryParams: {
                    ownerUid: this.ownerUid
                }
            })
            .then((response: MKHttpResponse) => {
                MKAzukiRequest.responseTime.registration = response.responseTime;
                if (response.status === 200 || (response.status >= 466 && response.status < 500)) {
                    // we have json data to process for these status codes...
                    return response.json();
                }
                // 4xx or 5xx HTTP Error
                return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.REGISTRATION.API, response.status));
            })
            .then((data: any) => {
                if (data.result === "success") {
                    return resolve();
                }
                // request failed, return error code and message
                return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.REGISTRATION.API, data.response.code, data.response.message));
            })
            .catch((error: any) => {
                if (error instanceof MKHttpError) {
                    return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.REGISTRATION.API, Number(error.code), error.message));
                }
                // unknown error
                MKLog.error("[registrationRequest] ", error);
                return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.REGISTRATION.API));
            });
        });
    }

    /**
     * Roll request.
     *
     * @returns Promise which will resolve to JSON data containing roll info; Promise will reject with an error object on failure.
     */
    async rollRequest(): Promise<MKRollInfo> {
        // Reset Azuki response time
        MKAzukiRequest.responseTime.roll = 0;
        
        // register if not registered...
        const savedAuthToken = this.localStorage.getRegistered();
        const currentAuthToken = this.headers["AuthorizationToken"];
        if (currentAuthToken && currentAuthToken !== savedAuthToken) {
            this.localStorage.clearRegistered();
            try {
                await this.registrationRequest();
                this.localStorage.setRegistered(currentAuthToken);
            } catch (error) {
                return Promise.reject(error);
            }
        }

        // Check and add device profile to headers
        if (!!!this.headers["DeviceProfile"]) {
            const deviceProfile = await MKPlatform.getDeviceProfile();
            if (!!deviceProfile) this.headers["DeviceProfile"] = deviceProfile;
        }

        // To check the widevine level to generate the Client keys.
        const decodedDeviceProfile = MKUtils.base64Decode(this.headers["DeviceProfile"]);
        const deviceInfo = JSON.parse(decodedDeviceProfile);
        const isWidevneLevel3 = "wvLevel" in deviceInfo && deviceInfo.wvLevel === "L3";
        const [major, minor] = this.headers["AzukiIMC"]?.match(/IMC(\d+)\.(\d+)(?:\.(\d+))?/)?.slice(1).map(Number) ?? [];
        const isIMCVersionValid = major > 7 || (major === 7 && minor >= 5);

        // Check and add the Client's public key as MessageId to headers
        if (this.playerConfig.enableWidevineEncryption && !!!this.headers["MessageId"] && isIMCVersionValid && isWidevneLevel3) {
            const millisecondsPerDay = 24 * 60 * 60 * 1000; // 24 hours
            const currentTimestamp: number = Date.now(); // current date
            let clientKeys: {
                publicKeyBase64: string;
                privateKeyBase64: string;
            };
            const keyData = this.localStorage.getClientKeys(); // fetch the exisitng client key
            if (keyData && currentTimestamp - keyData.timestamp < millisecondsPerDay) {
                clientKeys = {
                    publicKeyBase64: keyData.publicKey,
                    privateKeyBase64: keyData.privateKey
                };
            } else {
                clientKeys = MKUtils.generateECKeyPair();  // Generate new key
                this.localStorage.clearClientPrivatekey();
                this.localStorage.setClientKeys(clientKeys.privateKeyBase64, clientKeys.publicKeyBase64, currentTimestamp);
            }

            // SecureHeaders signature
            if (!!!this.headers["X-MK-Signature"] && !!clientKeys && this.kssUrl) {
                this.secureHeadersManager = new MKSecureHeadersManager(this.headers["AuthorizationToken"], this.kssUrl ,clientKeys?.publicKeyBase64, clientKeys?.privateKeyBase64);
                const kssKeys = await this.secureHeadersManager.getSignatureFromHeaders(this.headers);
                if (kssKeys.isWvLicenseEncrypted) {
                    this.headers["X-MK-Signature"] = kssKeys.Signature;

                    // Update the request headers with the client public key
                    const publicKey = clientKeys?.publicKeyBase64;
                    if (!!publicKey) this.headers["MessageId"] = publicKey;
                }  
            }
        }
        
        // notify pending beacons for the previous session
        await this.pendingBeaconRequest();

        // request query params
        const queryParams: MKRequestQueryParams = {
            mediaId: this.mediaId,
            ownerUid: this.ownerUid,
            sessionId: this.sessionId,
            enablelowlatency: this.lowLatency
        };

        // add offerId to query params if available
        if (!!this.offerId) queryParams["oid"] = this.offerId;

        // update query params to indicate dvr from rolling buffer if set
        if (this.isDvrFromRollingBuffer) {
            queryParams["is_dvr"] = this.isDvrFromRollingBuffer;
        } else {
            delete queryParams["is_dvr"];
        }

        // roll request
       return new Promise<MKRollInfo>((resolve, reject) => {
            this.rollcall(queryParams)
            .then(async (rollInfo: MKRollInfo) => {
                return resolve(rollInfo);
            }).catch((error) => { 
                // If the error code is 4-62-383 then retry the registration call
                if (error.code === "4-62-383" && !this.registrationRetryflag) {
                    MKLog.debug("Azuki roll call failed due to registration expiry, triggering registration");
                    this.localStorage.clearRegistered();
                    try {
                        this.registrationRequest();
                        this.localStorage.setRegistered(currentAuthToken);
                    } catch (error) {
                        return reject(error);
                    }
                    // Check if any pending beacons request 
                    this.pendingBeaconRequest();
                    this.registrationRetryflag = true;
                    // roll request after registration retry.
                    this.rollcall(queryParams)
                    .then(async (rollInfo: MKRollInfo) => {
                        return resolve(rollInfo)
                    });
                } else {
                    return reject(error);
                }
            });
        });
    }

    /**
     *  roll request call returns the rollInfo, and returns the reject on failure of error object
     * 
     *  @param queryParams includes the mediaId, ownerUid and sessionId.
     */
    async rollcall(queryParams: MKRequestQueryParams): Promise<MKRollInfo> {
        return new Promise<MKRollInfo>((resolve, reject) => {
            this.post(MKAzukiRequestEndpoint.ROLL.get(this.baseUrl), {
                headers: this.headers,
                queryParams: queryParams,
                body: {
                    roll: {
                        isLive: this.isLive,
                        rightsMode: this.rightsMode,
                        startTime: this.startTime,
                        locationDetail: MKAzukiRequest.locationDetail,
                        adobePrimeTimeConfig: this.adobePrimetimeConfig,
                        inhome: MKAzukiRequest.inhomeStatus ? "yes" : "no",
                        cloudIdInfo: MKAzukiRequest.cloudIdInfo
                    }
                }
            })
            .then((response: MKHttpResponse) => {
                MKAzukiRequest.responseTime.roll = response.responseTime;
                if (response.status === 200 || (response.status >= 466 && response.status < 500)) {
                    // we have json data to process for these status codes...
                    return response.json();
                }
                // 4xx or 5xx HTTP Error
                return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.ROLL.API, response.status));
            })
            .then((data: any) => {
                if (data.result === "success" ) {
                    // Delete the request headers MessageId to aviod sending the same to bitmovin
                    if ("MessageId" in this.headers) {
                        delete this.headers["MessageId"];
                    }
                    const rollInfo = MKAzukiResponseUtils.getRollInfo(data.response, this.baseUrl, queryParams, this.headers);
                    this.beaconInterval = rollInfo.beaconInterval;
                    rollInfo.beaconInterval > 0 ? this.saveBeaconData() : this.clearBeaconData();
                    if (this.registrationRetryflag === true) {
                        this.registrationRetryflag = false;
                    }
                    return resolve(rollInfo);
                }
                // request failed, return error code and message for widevine encryption feature
                if (data.response.code == 55) {
                    if (this.playerConfig.enableWidevineEncryption && !this.kssUrl){
                        const updatedMessage = data.response.message +" - KSS url is not provided!!";
                        return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.ROLL.API,data.response.code,updatedMessage));
                    } else {
                        const updatedMessage = data.response.message +" - Widevine encryption feature not enabled!!";
                        return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.ROLL.API,data.response.code,updatedMessage));
                    }
                }
                // request failed, return error code and message
                return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.ROLL.API, data.response.code, data.response.message));
            })
            .catch((error: any) => {
                if (error instanceof MKHttpError) {
                    return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.ROLL.API, Number(error.code), error.message));
                }
                // unknown error
                MKLog.error("[rollRequest] ", error);
                return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.ROLL.API));
            });
        });
    }

    /**
     * Get program information for the specifed time range.
     *
     * @param startDate query start date-time (ISO) string (Ex: 2023-01-01T01:00:00Z).
     * @param endDate query end date-time (ISO) string (Ex: 2023-01-01T01:00:00Z).
     * @returns Resolves to a promise containing list of program information or an empty list.
     */
    async programInfoRequest(startDate: string, endDate: string): Promise<MKProgramInfo[]> {
        return new Promise<MKProgramInfo[]>((resolve, _reject) => {
            this.get(MKAzukiRequestEndpoint.QUERY.get(this.baseUrl), {
                queryParams: {
                    channelId: this.mediaId,
                    ownerUid: this.ownerUid,
                    startDate: startDate,
                    endDate: endDate,
                    // defaults
                    pbr: true,
                    programs: true,
                    partialPrograms: true,
                    externalRecordings: false
                },
                headers: this.headers
            })
            .then((response: MKHttpResponse) => {
                MKAzukiRequest.responseTime.query = response.responseTime;
                if (response.ok) {
                    return response.json();
                }
                // resolve with an empty list on error
                return resolve([]);
            })
            .then((data: any) => {
                const programInfoList: MKProgramInfo[] = [];
                if (data.result === "success" && MKUtils.isArray(data.response.medias)) {
                    const programs: any[] = data.response.medias;
                    programs.forEach((program: any) => {
                        // get and append program info to the list
                        programInfoList.push(MKAzukiResponseUtils.getProgramInfo(program));
                    });
                }
                return resolve(programInfoList);
            })
            .catch(() => {
                // resolve with an empty list on error
                return resolve([]);
            })
        });
    }

    /**
     * Start azuki beacon notifications.
     * @returns a promise which will resolve on success and reject on error with appropriate error code and message.
     */
    async startSession(): Promise<void> {
        if (this.beaconInterval === 0) return;
        // notify the first beacon and then schedule the timer
        this.isPaused = false;
        return this.beaconRequest()
        .then(() => {
            this.scheduleBeaconRequest(this.beaconInterval * 1000);
        });
    }

    /**
     * Stop azuki beacon notifications and report the final beacon.
     * @returns a promise which will resolve on success and reject on error with appropriate error code and message.
     */
    async stopSession(): Promise<void> {
        if (this.beaconInterval === 0) return;
        // stop the timer first and notify a final beacon
        clearInterval(this.beaconTimer);
        return this.beaconRequest(true);
    }

    /**
     * Pause the Beacon calls.
     * Mainly used to pause the calls while the playback is being casted.
     */
    public PauseSession(): void {
        this.isPaused =  true;
        if (this.beaconInterval === 0) return;
        // stop the timer
        clearInterval(this.beaconTimer);
    }

    /**
     * Schedule azuki beacon notifications.
     * @param interval interval in seconds to notify azuki beacons for session concurrency.
     */
    private scheduleBeaconRequest(interval: number): void {
        if (this.beaconTimer) clearInterval(this.beaconTimer);
        this.beaconTimer = setInterval(() => {
            this.beaconRequest()
            .catch((error) => {
                // we don't intend to stop reporting beacons for errors
                // only for specific errors the beacons will be stopped
                // so do not disturb the timer, just report the error
                if (this.errorCallback) this.errorCallback(error);
            });
        }, interval);
    }

    /**
     * Send a beacon notification request.
     * 
     * @param isFinalBeacon Indicates a final complete beacon when `true`.
     * @returns a promise which will resolve when successful and reject with error on failure.
     */
    private async beaconRequest(isFinalBeacon: boolean = false): Promise<void> {
        //Sends the user Engagement Data
        if (MKAzukiRequest.enableUserEngagementData) {
            const userEngagementHeader: any = {
                sessionId: this.sessionId,
                mediaId: this.mediaId,  
                tenantId: this.ownerUid
            };
            if(MKAzukiRequest.startPlaybackPosition !== undefined && !this.isLive) {
                userEngagementHeader[MKConstants.StartPlaybackPosition] = MKAzukiRequest.startPlaybackPosition;
            }
            if(MKAzukiRequest.relativePlaybackLocation !== undefined && !this.isLive) {
                userEngagementHeader[MKConstants.RelativePlaybackLocation] = MKAzukiRequest.relativePlaybackLocation;
            }
            if(MKAzukiRequest.playerState) {
                userEngagementHeader[MKConstants.XPlayerState] = MKAzukiRequest.playerState;
            }
            Object.assign(userEngagementHeader, MKAzukiRequest.userEngagementData);
            this.headers[MKConstants.XUserEngagementData] = MKUtils.base64Encode(JSON.stringify(userEngagementHeader));
            this.saveBeaconData();
        }
        return new Promise<void>((resolve, reject) => {
            this.post(MKAzukiRequestEndpoint.BEACONS.get(this.baseUrl), {
                body: {
                    beacon: {
                        isLive: this.isLive,
                        complete: isFinalBeacon,
                        inhome: MKAzukiRequest.inhomeStatus ? "yes" : "no"
                    }
                },
                queryParams: {
                    mediaId: this.mediaId,
                    ownerUid: this.ownerUid,
                    sessionId: this.sessionId
                },
                headers: this.headers
            })
            .then((response: MKHttpResponse) => {
                MKAzukiRequest.responseTime.beacons = response.responseTime;
                if (response.status === 200 || (response.status >= 466 && response.status < 500)) {
                    // we have json data to process for these status codes...
                    return response.json();
                }
                // 4xx or 5xx HTTP Error
                return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.BEACONS.API, response.status));
            })
            .then((data: any) => {
                if (data.result === "success" ) {
                    const beaconResponse: any = data.response;
                    const beaconInterval: number = beaconResponse.interval ?? 0;
                    if (isFinalBeacon && beaconInterval === 0) {
                        this.clearBeaconData();
                    }
                    return resolve();
                }
                // request failed, return error code and message
                return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.BEACONS.API, data.response.code, data.response.message));
            })
            .catch((error: any) => {
                if (error instanceof MKHttpError) {
                    return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.BEACONS.API, Number(error.code), error.message));
                }
                // unknown error
                MKLog.error("[beaconRequest] ", error);
                return reject(new MKAzukiRequestError(MKAzukiRequestEndpoint.BEACONS.API));
            });
        });
    }

    /**
     * Notify pending beacon if available.
     */
    private async pendingBeaconRequest(): Promise<void> {
        MKAzukiRequest.responseTime.pendingBeacons = 0;
        const beaconData = this.localStorage.getBeaconData();
        if (beaconData && !!beaconData.requestUrl && beaconData.requestOptions) {
            try {
                const response = await this.post(beaconData.requestUrl, beaconData.requestOptions);
                MKAzukiRequest.responseTime.pendingBeacons = response.responseTime;
                MKLog.debug("Pending beacon notification ", response.ok ? "successful." : "failed.");
                this.clearBeaconData(); // clear saved beacon data
            } catch (error) {
                MKLog.debug("Pending beacon notification failed with error: ", error);
                this.clearBeaconData(); // clear saved beacon data
            }
        }
    }

    /**
     * Update Azuki Request Headers for the given source config.
     * @param sourceConfig the registered source configuration.
     * @returns the request headers object.
     */
    private getRequestHeaders(sourceConfig: MKSourceConfig): MKHeaders {
        const requestHeaders: MKHeaders = {}
        if (sourceConfig.registered) {
            requestHeaders["AzukiIMC"] = MKConstants.AzukiIMC;
            requestHeaders["Content-Type"] = "text/plain";
            requestHeaders["AuthorizationToken"] = sourceConfig.registered.environmentConfig.authToken;

            // update application token if set.
            if (sourceConfig.registered.applicationToken) {
                requestHeaders["ApplicationToken"] = sourceConfig.registered.applicationToken;
            }

            // append location details if specified
            if (MKAzukiRequest.locationDetail) {
                requestHeaders["X-LocationDetails"] = MKAzukiRequest.locationDetail;
            }
        }
        if (MKAzukiRequest.customHeaders && Object.keys(MKAzukiRequest.customHeaders).length > 0) {
            Object.assign(requestHeaders, MKAzukiRequest.customHeaders);
        }
        return requestHeaders;
    }

    /**
     * Save beacon data to local storage
     */
    private saveBeaconData(): void {
        const beaconData = {
            requestUrl: MKAzukiRequestEndpoint.BEACONS.get(this.baseUrl),
            requestOptions: {
                body: {
                    beacon: {
                        complete: true,
                        isLive: this.isLive,
                        inhome: MKAzukiRequest.inhomeStatus ? "yes" : "no"
                    }
                },
                queryParams: {
                    mediaId: this.mediaId,
                    ownerUid: this.ownerUid,
                    sessionId: this.sessionId
                },
                headers: this.headers
            }
        };
        // save to local storage
        this.localStorage.setBeaconData(beaconData);
    }

    private clearBeaconData(): void {
        if(MKAzukiRequest.enableUserEngagementData){
            this.resetUserEngagementData();
        }
        this.localStorage.clearBeaconData();
    }

    /**
     * Reset the user Engagement Data
     */
    private resetUserEngagementData(){
        MKAzukiRequest.relativePlaybackLocation = -1;
        MKAzukiRequest.startPlaybackPosition = 0;
        MKAzukiRequest.playerState = "";
    }
}